/**
 * TASK-TMS-005: ThemeAssetLoader Implementation
 *
 * Manages asynchronous loading of theme assets with progress tracking,
 * texture caching, and fallback placeholder generation.
 */

import * as PIXI from 'pixi.js';
import type { TerrainTheme, ThemeLoadState } from '../../types/terrain';
import {
  createColorTexture,
  createRadialGradientTexture,
  createPointTexture,
  createGridPatternTexture,
  createGradientTexture,
} from '../../utils/placeholder-textures';

/**
 * ThemeAssetLoader
 *
 * Handles loading of theme textures with caching and progress reporting.
 * Provides placeholder texture generation for fallback scenarios.
 *
 * @example
 * ```typescript
 * const loader = new ThemeAssetLoader();
 *
 * // Load theme with progress callback
 * const textures = await loader.loadTheme(theme, (progress) => {
 *   console.log(`Loading: ${(progress * 100).toFixed(0)}%`);
 * });
 *
 * // Check if loaded
 * if (loader.isLoaded('cyber-arena')) {
 *   console.log('Theme ready!');
 * }
 *
 * // Fallback to placeholders
 * const placeholders = loader.generatePlaceholderTextures(theme);
 * ```
 */
export class ThemeAssetLoader {
  /** Map of theme IDs to their load state */
  private loadedThemes: Map<string, ThemeLoadState> = new Map();

  /** Cache of textures by URL to avoid redundant loading */
  private textureCache: Map<string, PIXI.Texture> = new Map();

  /**
   * Load all assets for a theme asynchronously.
   *
   * This method loads all textures defined in the theme's assets configuration,
   * caching them to avoid redundant loads. If the theme has already been loaded,
   * the cached textures are returned immediately.
   *
   * @param theme - The terrain theme to load
   * @param onProgress - Optional callback invoked with progress (0-1) as assets load
   * @returns Promise resolving to a record of loaded textures keyed by asset name
   * @throws Error if any asset fails to load
   *
   * @example
   * ```typescript
   * try {
   *   const textures = await loader.loadTheme(theme, (progress) => {
   *     updateProgressBar(progress * 100);
   *   });
   *   console.log('Theme loaded successfully');
   * } catch (error) {
   *   console.error('Failed to load theme:', error);
   *   // Fall back to placeholders
   *   const textures = loader.generatePlaceholderTextures(theme);
   * }
   * ```
   */
  async loadTheme(
    theme: TerrainTheme,
    onProgress?: (progress: number) => void
  ): Promise<Record<string, PIXI.Texture>> {
    // Check if theme is already loaded
    const cached = this.loadedThemes.get(theme.themeId);
    if (cached?.status === 'loaded' && cached.textures) {
      return cached.textures as Record<string, PIXI.Texture>;
    }

    // Initialize load state
    const state: ThemeLoadState = {
      themeId: theme.themeId,
      status: 'loading',
      progress: 0,
    };
    this.loadedThemes.set(theme.themeId, state);

    // Get placeholder textures as fallbacks
    const placeholders = this.generatePlaceholderTextures(theme);
    const textures: Record<string, PIXI.Texture> = { ...placeholders };

    // Get all asset entries (filter out undefined optional assets)
    const assetEntries = Object.entries(theme.assets).filter(
      ([_, url]) => url !== undefined
    ) as Array<[string, string]>;

    let loadedCount = 0;
    let failedCount = 0;

    // Load each asset sequentially with progress updates
    // Use try/catch per asset to be resilient to individual failures
    for (let i = 0; i < assetEntries.length; i++) {
      const [key, url] = assetEntries[i];

      try {
        // Check texture cache first
        if (this.textureCache.has(url)) {
          textures[key] = this.textureCache.get(url)!;
          loadedCount++;
        } else {
          // Load texture from URL
          const texture = await PIXI.Assets.load<PIXI.Texture>(url);
          this.textureCache.set(url, texture);
          textures[key] = texture;
          loadedCount++;
        }
      } catch (error) {
        // Log warning but continue - the placeholder is already in textures
        console.warn(
          `[ThemeAssetLoader] Failed to load ${key} from ${url}, using placeholder:`,
          error instanceof Error ? error.message : error
        );
        failedCount++;
        // Placeholder is already set from initial spread
      }

      // Update progress
      state.progress = (i + 1) / assetEntries.length;
      onProgress?.(state.progress);
    }

    // Mark as loaded (with potential partial failures)
    state.status = 'loaded';
    state.textures = textures;

    if (failedCount > 0) {
      console.log(
        `[ThemeAssetLoader] Theme '${theme.themeId}' loaded with ${loadedCount}/${assetEntries.length} assets (${failedCount} using placeholders)`
      );
    } else {
      console.log(
        `[ThemeAssetLoader] Theme '${theme.themeId}' loaded successfully (${loadedCount} assets)`
      );
    }

    return textures;
  }

  /**
   * Get the current load state for a theme.
   *
   * @param themeId - The theme ID to query
   * @returns The load state if found, undefined otherwise
   *
   * @example
   * ```typescript
   * const state = loader.getLoadState('cyber-arena');
   * if (state) {
   *   console.log(`Status: ${state.status}, Progress: ${state.progress * 100}%`);
   *   if (state.error) {
   *     console.error('Error:', state.error);
   *   }
   * }
   * ```
   */
  getLoadState(themeId: string): ThemeLoadState | undefined {
    return this.loadedThemes.get(themeId);
  }

  /**
   * Check if a theme has been successfully loaded.
   *
   * @param themeId - The theme ID to check
   * @returns True if the theme is fully loaded, false otherwise
   *
   * @example
   * ```typescript
   * if (loader.isLoaded('cyber-arena')) {
   *   startBattle();
   * } else {
   *   showLoadingScreen();
   * }
   * ```
   */
  isLoaded(themeId: string): boolean {
    return this.loadedThemes.get(themeId)?.status === 'loaded';
  }

  /**
   * Generate placeholder textures for a theme.
   *
   * This method creates procedurally generated textures that can be used
   * as fallbacks when real theme assets fail to load or during development.
   * The placeholders are generated using the theme's color palette.
   *
   * @param theme - The theme to generate placeholders for
   * @returns Record of placeholder textures keyed by asset name
   *
   * @example
   * ```typescript
   * // Use placeholders during development
   * const placeholders = loader.generatePlaceholderTextures(theme);
   * terrainRenderer.initialize(placeholders);
   *
   * // Or use as fallback
   * try {
   *   const textures = await loader.loadTheme(theme);
   * } catch {
   *   const textures = loader.generatePlaceholderTextures(theme);
   * }
   * ```
   */
  generatePlaceholderTextures(theme: TerrainTheme): Record<string, PIXI.Texture> {
    return {
      farBackground: createColorTexture(theme.colors.ambientLight, 256, 256),
      midBackground: createGradientTexture(theme.colors.ambientLight, 256, 256),
      nearBackground: createGridPatternTexture(theme.colors.gridGlow, 64, 64),
      fogParticle: createRadialGradientTexture(0xFFFFFF, 64, 64),
      dustParticle: createPointTexture(0xFFFFFF, 16, 16),
    };
  }
}
